import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { signIn, signInTrial, clearAuthError } from '../../store/authSlice';
import { trialAccounts } from '../../services/qbaseClient';
import { QBASE_SYS_ID } from '../../config/qbase';
import { IS_LOCAL } from '../../config/mode';
import { useTranslation } from 'react-i18next';
import { BE1_API } from '../../config/api';
import { formatBytes, formatDateTime, formatNumber } from '../../utils/formatters';

/** 서버의 스탠드얼론 내려받기 목록 (backend services/downloads) */
interface DownloadFile {
  filename: string; label?: string; description?: string; exists: boolean; size: number;
  downloadCount?: number; lastDownloadAt?: string | null;
}
interface DownloadManifest { version?: string; released?: string; built?: string; files: DownloadFile[] }

/**
 * QBase 로그인 — 오픈플로우 시스템(REACT_APP_QBASE_SYS_ID) 소속 계정으로 로그인한다.
 * 계정은 QBase 에서 관리한다 (프로비저닝 시 만든 관리자, 또는 QBase 관리 화면에서 추가한 사용자).
 */
const SignIn: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { status, error } = useSelector((s: RootState) => s.auth);
  const [loginId, setLoginId] = useState('');
  const [pwd, setPwd] = useState('');
  const busy = status === 'checking';
  // 스탠드얼론(무설치 윈도우판) 내려받기 — 서버에 올려 둔 꾸러미가 있을 때만 카드 아래에 보인다 (LiveSQL 로그인 화면과 같은 방식).
  // 목록이 없거나 서버가 응답하지 않으면 조용히 숨긴다 — 로그인을 방해하면 안 된다.
  const [downloads, setDownloads] = useState<DownloadManifest | null>(null);
  // 체험 계정 리본 — 누르면 비밀번호 없이 그 계정으로 들어간다(서버가 대신 로그인). 스탠드얼론에는 없다
  const [trials, setTrials] = useState<string[]>([]);
  const [trialPicked, setTrialPicked] = useState<string | null>(null);
  useEffect(() => {
    if (IS_LOCAL) return undefined;
    let alive = true;
    trialAccounts().then((a) => { if (alive) setTrials(a); });
    return () => { alive = false; };
  }, []);
  const tryTrial = (id: string) => {
    if (busy) return;
    setTrialPicked(id);
    dispatch(clearAuthError());
    dispatch(signInTrial(id));
  };
  useEffect(() => {
    let alive = true;
    fetch(`${BE1_API}/api/downloads/manifest`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d?.files?.some((f: DownloadFile) => f.exists)) setDownloads(d); })
      .catch(() => { /* 내려받을 것이 없다 */ });
    return () => { alive = false; };
  }, []);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginId.trim() || !pwd) return;
    dispatch(clearAuthError());
    dispatch(signIn({ loginId: loginId.trim(), pwd }));
  };

  return (
    <div className="flex items-center justify-center h-screen bg-gray-50">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-lg shadow-md p-8 space-y-4">
        <div>
          <h1 className="text-2xl font-bold text-center">OpenFlow</h1>
          <p className="text-xs text-gray-400 text-center mt-1">{IS_LOCAL ? t('layout:auth.localMode') : t('layout:auth.tenant', { sysId: QBASE_SYS_ID })}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="loginId">{t('layout:auth.loginId')}</label>
          <input id="loginId" autoFocus autoComplete="username" value={loginId}
            onChange={e => setLoginId(e.target.value)}
            className="w-full px-3 py-2 text-xl border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="pwd">{t('layout:auth.password')}</label>
          <input id="pwd" type="password" autoComplete="current-password" value={pwd}
            onChange={e => setPwd(e.target.value)}
            className="w-full px-3 py-2 text-xl border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
        <button type="submit" disabled={busy || !loginId.trim() || !pwd}
          className="w-full py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
          {busy ? t('layout:auth.checking') : t('layout:auth.signIn')}
        </button>
        {trials.length > 0 && (
          <div className="pt-3 border-t border-gray-200">
            <div className="text-xs font-semibold text-gray-600 mb-1.5">{t('layout:auth.trial.title')}</div>
            <div className="flex flex-wrap gap-1.5">
              {trials.map((id) => (
                <button key={id} type="button" onClick={() => tryTrial(id)} disabled={busy}
                  title={t('layout:auth.trial.pickTitle', { id })}
                  className="px-3 py-1 text-xs rounded-full border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50">
                  {busy && trialPicked === id ? t('layout:auth.checking') : id}
                </button>
              ))}
            </div>
            <div className="text-[0.6875rem] text-gray-500 mt-1.5">{t('layout:auth.trial.hint')}</div>
          </div>
        )}
        {downloads && (
          <div className="pt-4 mt-2 border-t border-gray-200">
            <div className="text-sm font-semibold text-gray-700 mb-2">
              {t('layout:auth.download.title')}
              {downloads.version && <span className="ml-1.5 text-xs font-normal text-gray-500">v{downloads.version}</span>}
              {(downloads.built || downloads.released) && (
                <span className="ml-1.5 text-xs font-normal text-gray-500">{t('layout:auth.download.built', { when: formatDateTime(downloads.built || downloads.released, { seconds: false }) })}</span>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              {downloads.files.filter((f) => f.exists).map((f) => (
                <a key={f.filename} href={`${BE1_API}/api/downloads/${encodeURIComponent(f.filename)}`} download
                  className="block px-3 py-2 rounded-md border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 no-underline">
                  <span className="text-sm font-semibold">⬇ {f.label || f.filename}</span>
                  {f.size > 0 && <span className="ml-1.5 text-xs text-gray-500">({formatBytes(f.size)})</span>}
                  {!!f.downloadCount && (
                    <span className="ml-1.5 text-xs text-gray-500"
                      title={f.lastDownloadAt ? t('layout:auth.download.lastAt', { when: formatDateTime(f.lastDownloadAt, { seconds: false }) }) : undefined}>
                      {t('layout:auth.download.count', { n: formatNumber(f.downloadCount) })}
                    </span>
                  )}
                  {f.description && <span className="block text-xs text-gray-600 mt-0.5">{f.description}</span>}
                </a>
              ))}
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default SignIn;
